
























































































import {
  Vue,
  Prop,
  Component,
  Watch
} from "@/shared/vuepack";
import {
  Advisor3Rule,
  AdvisorDeliveryChannel,
  UserPermission
} from "@/shared/types";
import { AdvisorAction } from "@/admin/types";
import { codemirror } from "vue-codemirror";
import sql from "codemirror/mode/sql/sql";
import * as RouteNames from "@/admin/route-names";
import { useAdvisorStore } from "@/admin/store/advisor";
import { useNotifierStore } from "@/shared/store/notifier";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useRolePermissionsStore } from "@/shared/store/rolePermissions";
import { Validator } from "vee-validate";

@Component({
  components: {
    codemirror,
    sql // import required for code highlighting
  }
})
export default class RuleEditor3 extends Vue {
  $validator: Validator;

  advisorStore = useAdvisorStore();
  notifierStore = useNotifierStore();
  featureFlagStore = useFeatureFlagStore();
  rolePermissionStore = useRolePermissionsStore();
  AdvisorRuleHelp = RouteNames.AdvisorRuleHelp;

  @Prop()
  ruleId: number;
  requiredQueryPart = "from userservice.user as advisoruser";

  model: Advisor3Rule = {
    id: null,
    name: "",
    query: "select distinct AdvisorUser.CognitoUsername\n" +
        "from userservice.User as AdvisorUser;",
    actionIds: []
  };

  codemirrorOptions = {
    mode: "sql",
    theme: "dracula",
    indentWithTabs: true,
    smartIndent: true,
    lineNumbers: true,
    matchBrackets: true,
    autofocus: true
  };

  RuleNameAllowedCharacters = {
    getMessage () {
      return "Text can only include letters, numbers and the following special characters: _-.";
    },
    validate (value) {
      return new RegExp(/^[a-zA-Z0-9_\-.]*$/).test(value);
    }
  };

  showPreviewArea = false;
  isValidQuery = false;
  isModalVisible = false;
  selectedActions: AdvisorAction[] = [];

  cancelDelete () {
    this.isModalVisible = false;
  }

  deleteRule () {
    if (!this.model.id) return;

    this.advisorStore.softDelete(this.model.id)
        .then(() => {
          this.model = { id: null, name: "", query: "", actionIds: null };
          this.$router.push({ name: RouteNames.AdvisorRuleList3 });
        });
  }

  get hasEditPermission (): boolean {
    return this.featureFlagStore.flags.hsa4475allowRolePermissions
        ? this.rolePermissionStore.userHasAdminPermission(UserPermission.AdminPortalAdvisorEdit)
        : true;
  }

  get hasId () {
    return !!this.model.id;
  }

  @Watch("ruleId")
  onRuleIdChange () {
    this.load();
    this.advisorStore.rulePreviewResult.data = {} as any;
    this.advisorStore.rulePreviewResult.error = null;
  }

  @Watch("advisorStore.rule", { deep: true })
  onRuleChange () {
    this.model.query = this.advisorStore.rule.query;
    this.model.id = this.advisorStore.rule.id;
    this.model.name = this.advisorStore.rule.name;
  }

  load () {
    if (this.ruleId !== null && this.ruleId !== undefined) {
      this.advisorStore.loadRule(this.ruleId)
          .then(r => {
            if (!r) {
              this.notifierStore.setDanger("Rule could not be found");
              this.model = {
                id: null,
                name: "",
                query: "select distinct AdvisorUser.CognitoUsername\n" +
                    "from userservice.User as AdvisorUser;",
                actionIds: null
              };
              this.$router.push({ name: RouteNames.AdvisorRule3 });
            }
            if (r.actionIds) {
              this.selectedActions = [];
              r.actionIds.forEach(element => {
                this.selectedActions.push(this.advisorStore.actions.find(x => x.actionId === element));
              });
            }
          });
    }
  }

  mounted () {
    this.advisorStore.loadAllActions().then(() => this.load());
    this.$validator.extend("RuleNameAllowedCharacters", this.RuleNameAllowedCharacters);
  }

  onCmCodeChange (value) {
    this.model.query = value;
    this.isValidQuery = false;
  }

  validateRequiredParts () {
    const hasRequiredPart = this.model.query.toLowerCase().indexOf(this.requiredQueryPart) !== -1;
    if (!hasRequiredPart) {
      this.notifierStore.setDanger("Query does not contain required part: '" + this.requiredQueryPart + "'");
      return false;
    }
    // validating query for cognitousername. Rule can't be executed without this field
    const cleanQuery = this.model.query.toLowerCase().trim().replace("/\r?\n|\r/", "");
    // starting after select (length 6) clause
    const attributes = cleanQuery.substring(6, cleanQuery.indexOf("from")).split(",");

    let hasCognitoUsername = false;
    // aliases are removed for clean comparison
    if (attributes !== null) {
      for (const attribute of attributes) {
        let trimmedCol = attribute.trim();
        if (trimmedCol.indexOf(".") > 0) {
          trimmedCol = trimmedCol.substring(trimmedCol.indexOf(".") + 1);
        }
        if (trimmedCol.lastIndexOf(" ") > 0) {
          trimmedCol = trimmedCol.substring(trimmedCol.lastIndexOf(" ") + 1);
        }
        if (trimmedCol === "cognitousername") {
          hasCognitoUsername = true;
          break;
        }
      }
    }

    if (!hasCognitoUsername) {
      this.notifierStore.setDanger("Query does not contain required column: CognitoUsername.");
      return false;
    }

    return true;
  }

  onSubmit () {
    this.$validator
        .validate()
        .then(result => {
          if (result && this.validateRequiredParts()) {
            this.model.actionIds = [ ...this.selectedActions.map(x => x.actionId) ];
            this.advisorStore.saveRule(this.model).then(() => {
              this.$router.push({ name: RouteNames.AdvisorRule3, params: { ruleId: this.model.id.toString() } });
            });
          }
        });
  }

  onPreview () {
    this.$validator
        .validate()
        .then(result => {
          if (result && this.validateRequiredParts()) {
            this.showPreviewArea = true;
            this.advisorStore.previewRule(this.model).then(() => {
              this.isValidQuery = this.advisorStore.rulePreviewResult.error === null;
            });
          }
        });
  }

  showModal () {
    this.isModalVisible = true;
  }

  get submitDisabled () {
    return !this.isValidQuery;
  }

  updateActions (event) {
    const index = this.selectedActions.indexOf(event);

    if (index > 0) {
      this.selectedActions.splice(index, 1);
      return;
    }
    this.selectedActions.push(event);
  }

  removeAction (action) {
    const index = this.selectedActions.indexOf(action);
    if (index > -1) {
      this.selectedActions.splice(index, 1);
      return;
    }
  }

  actionsToDisplay () {
    return this.advisorStore.actions.filter(x => !this.selectedActions.includes(x));
  }

  actionDisplayName (action) {
    const baseMessage = `${action.name} :: ${action.deliveryChannel}`;
    if (action.deliveryChannel === AdvisorDeliveryChannel.AdvisorPopup) {
      return `${baseMessage}::${action.deliveryPage}`;
    }
    return baseMessage;
  }
}
